import { z } from 'zod';

// ============================================================================
// Availability (P4A-01)
// ============================================================================

export const ResellerTicketTypeAvailabilitySchema = z.object({
  id: z.string(),
  name: z.string(),
  category: z.string(),
  available: z.number().int(),
  price: z.number(),
  currency: z.literal('IDR'),
  soldOut: z.boolean(),
});
export type ResellerTicketTypeAvailability = z.infer<typeof ResellerTicketTypeAvailabilitySchema>;

export const ResellerDayAvailabilitySchema = z.object({
  date: z.string(),
  ticketTypes: z.array(ResellerTicketTypeAvailabilitySchema),
});
export type ResellerDayAvailability = z.infer<typeof ResellerDayAvailabilitySchema>;

export const ResellerAvailabilityResponseSchema = z.object({
  dateFrom: z.string(),
  dateTo: z.string(),
  availability: z.array(ResellerDayAvailabilitySchema),
  generatedAt: z.string(),
});
export type ResellerAvailabilityResponse = z.infer<typeof ResellerAvailabilityResponseSchema>;

export const ResellerAvailabilityParamsSchema = z.object({
  dateFrom: z.string(),
  dateTo: z.string(),
  ticketType: z.string().optional(),
});
export type ResellerAvailabilityParams = z.infer<typeof ResellerAvailabilityParamsSchema>;

// ============================================================================
// Settlements (P4A-02)
// ============================================================================

export const ResellerSettlementStatusSchema = z.enum(['DRAFT', 'APPROVED', 'PAID']);
export type ResellerSettlementStatus = z.infer<typeof ResellerSettlementStatusSchema>;

export const ResellerSettlementSchema = z.object({
  id: z.string(),
  partnerId: z.string(),
  periodStart: z.string(),
  periodEnd: z.string(),
  totalAmount: z.number(),
  commissionCount: z.number().int(),
  status: ResellerSettlementStatusSchema,
  paidAt: z.string().nullable(),
  invoiceRef: z.string().nullable(),
  createdAt: z.string(),
});
export type ResellerSettlement = z.infer<typeof ResellerSettlementSchema>;

const PaginationMetaSchema = z.object({
  page: z.number().int(),
  limit: z.number().int(),
  total: z.number().int(),
  totalPages: z.number().int(),
});

export const ResellerSettlementsResponseSchema = z.object({
  data: z.array(ResellerSettlementSchema),
  meta: PaginationMetaSchema,
});
export type ResellerSettlementsResponse = z.infer<typeof ResellerSettlementsResponseSchema>;

export const ResellerSettlementCommissionSchema = z.object({
  id: z.string(),
  bookingId: z.string(),
  bookingRef: z.string().nullable(),
  visitDate: z.string().nullable(),
  bookingTotal: z.number().nullable(),
  bookingStatus: z.string().nullable(),
  amount: z.number(),
  rate: z.number(),
  status: z.string(),
  settledAt: z.string().nullable(),
  createdAt: z.string(),
});
export type ResellerSettlementCommission = z.infer<typeof ResellerSettlementCommissionSchema>;

export const ResellerSettlementDetailResponseSchema = z.object({
  settlement: ResellerSettlementSchema,
  commissions: z.array(ResellerSettlementCommissionSchema),
});
export type ResellerSettlementDetailResponse = z.infer<typeof ResellerSettlementDetailResponseSchema>;

export const ResellerSettlementsParamsSchema = z.object({
  page: z.coerce.number().int().positive().optional(),
  limit: z.coerce.number().int().positive().max(100).optional(),
  status: z.string().optional(),
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
});
export type ResellerSettlementsParams = z.infer<typeof ResellerSettlementsParamsSchema>;

// ============================================================================
// Quotas (P4A-05)
// ============================================================================

export const ResellerQuotaSnapshotSchema = z.object({
  allocated: z.number().int(),
  used: z.number().int(),
  remaining: z.number().int(),
  date: z.string().optional(),
  month: z.string().optional(),
});
export type ResellerQuotaSnapshot = z.infer<typeof ResellerQuotaSnapshotSchema>;

export const ResellerCurrentQuotaResponseSchema = z.object({
  daily: ResellerQuotaSnapshotSchema.nullable(),
  monthly: ResellerQuotaSnapshotSchema.nullable(),
});
export type ResellerCurrentQuotaResponse = z.infer<typeof ResellerCurrentQuotaResponseSchema>;

export const ResellerQuotaTypeSchema = z.enum(['DAILY', 'MONTHLY']);
export type ResellerQuotaType = z.infer<typeof ResellerQuotaTypeSchema>;

export const ResellerQuotaRecordSchema = z.object({
  id: z.string(),
  partnerId: z.string(),
  quotaType: ResellerQuotaTypeSchema,
  periodDate: z.string(),
  allocated: z.number().int(),
  used: z.number().int(),
  remaining: z.number().int(),
  utilizationPercent: z.number(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ResellerQuotaRecord = z.infer<typeof ResellerQuotaRecordSchema>;

export const ResellerQuotaHistoryResponseSchema = z.object({
  data: z.array(ResellerQuotaRecordSchema),
  summary: z.object({
    totalAllocated: z.number().int(),
    totalUsed: z.number().int(),
    utilizationRate: z.number(),
  }),
  meta: PaginationMetaSchema,
});
export type ResellerQuotaHistoryResponse = z.infer<typeof ResellerQuotaHistoryResponseSchema>;

export const ResellerQuotaHistoryParamsSchema = z.object({
  page: z.coerce.number().int().positive().optional(),
  limit: z.coerce.number().int().positive().max(100).optional(),
  quotaType: z.string().optional(),
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
});
export type ResellerQuotaHistoryParams = z.infer<typeof ResellerQuotaHistoryParamsSchema>;

// ============================================================================
// Customers (P4B-01)
// ============================================================================

export const ResellerCustomerSummarySchema = z.object({
  totalCustomers: z.number().int(),
  repeatRate: z.number(),
  avgSpend: z.number(),
});
export type ResellerCustomerSummary = z.infer<typeof ResellerCustomerSummarySchema>;

export const ResellerCustomerRecordSchema = z.object({
  customerId: z.string(),
  name: z.string(),
  email: z.string().nullable(),
  bookingCount: z.number().int(),
  totalSpent: z.number(),
  lastVisit: z.string().nullable(),
  firstBooking: z.string(),
});
export type ResellerCustomerRecord = z.infer<typeof ResellerCustomerRecordSchema>;

export const ResellerCustomersResponseSchema = z.object({
  data: z.array(ResellerCustomerRecordSchema),
  summary: ResellerCustomerSummarySchema,
  meta: PaginationMetaSchema,
});
export type ResellerCustomersResponse = z.infer<typeof ResellerCustomersResponseSchema>;

export const ResellerCustomersParamsSchema = z.object({
  page: z.coerce.number().int().positive().optional(),
  limit: z.coerce.number().int().positive().max(100).optional(),
  search: z.string().optional(),
  sortBy: z.enum(['totalSpent', 'bookingCount', 'lastVisit']).optional(),
  sortOrder: z.enum(['asc', 'desc']).optional(),
});
export type ResellerCustomersParams = z.infer<typeof ResellerCustomersParamsSchema>;

export const ResellerCustomerBookingSchema = z.object({
  bookingId: z.string(),
  bookingRef: z.string(),
  ticketType: z.string(),
  visitDate: z.string(),
  status: z.string(),
  totalAmount: z.number(),
  commissionAmount: z.number(),
  quantity: z.number().int(),
  createdAt: z.string(),
});
export type ResellerCustomerBooking = z.infer<typeof ResellerCustomerBookingSchema>;

export const ResellerCustomerBookingsResponseSchema = z.object({
  bookings: z.array(ResellerCustomerBookingSchema),
  meta: PaginationMetaSchema,
});
export type ResellerCustomerBookingsResponse = z.infer<typeof ResellerCustomerBookingsResponseSchema>;

export const ResellerCustomerBookingsParamsSchema = z.object({
  page: z.coerce.number().int().positive().optional(),
  limit: z.coerce.number().int().positive().max(100).optional(),
});
export type ResellerCustomerBookingsParams = z.infer<typeof ResellerCustomerBookingsParamsSchema>;

// ============================================================================
// Announcements (P4B-03)
// ============================================================================

export const AnnouncementCategorySchema = z.enum([
  'OPERATIONAL',
  'PRICING',
  'FEATURE',
  'MAINTENANCE',
]);
export type AnnouncementCategory = z.infer<typeof AnnouncementCategorySchema>;

export const AnnouncementPrioritySchema = z.enum(['NORMAL', 'IMPORTANT', 'CRITICAL']);
export type AnnouncementPriority = z.infer<typeof AnnouncementPrioritySchema>;

export const ResellerAnnouncementSchema = z.object({
  id: z.string(),
  title: z.string(),
  summary: z.string(),
  category: AnnouncementCategorySchema,
  priority: AnnouncementPrioritySchema,
  requiresAck: z.boolean(),
  acknowledged: z.boolean(),
  acknowledgedAt: z.string().nullable(),
  publishedAt: z.string(),
  createdAt: z.string(),
});
export type ResellerAnnouncement = z.infer<typeof ResellerAnnouncementSchema>;

export const ResellerAnnouncementDetailSchema = ResellerAnnouncementSchema.extend({
  content: z.string(),
});
export type ResellerAnnouncementDetail = z.infer<typeof ResellerAnnouncementDetailSchema>;

export const ResellerAnnouncementsResponseSchema = z.object({
  data: z.array(ResellerAnnouncementSchema),
  meta: PaginationMetaSchema,
});
export type ResellerAnnouncementsResponse = z.infer<typeof ResellerAnnouncementsResponseSchema>;

export const ResellerAnnouncementsParamsSchema = z.object({
  page: z.coerce.number().int().positive().optional(),
  limit: z.coerce.number().int().positive().max(100).optional(),
  category: z.string().optional(),
});
export type ResellerAnnouncementsParams = z.infer<typeof ResellerAnnouncementsParamsSchema>;

// ============================================================================
// Support Tickets (reseller-facing) (P4B-05)
// Note: Prefixed Reseller* to avoid conflict with support/index.ts exports
// ============================================================================

export const ResellerTicketStatusSchema = z.enum([
  'OPEN',
  'IN_PROGRESS',
  'RESOLVED',
  'CLOSED',
]);
export type ResellerTicketStatus = z.infer<typeof ResellerTicketStatusSchema>;

export const ResellerTicketPrioritySchema = z.enum(['LOW', 'MEDIUM', 'HIGH']);
export type ResellerTicketPriority = z.infer<typeof ResellerTicketPrioritySchema>;

export const ResellerSupportTicketSummarySchema = z.object({
  openCount: z.number().int(),
  resolvedThisMonth: z.number().int(),
});
export type ResellerSupportTicketSummary = z.infer<typeof ResellerSupportTicketSummarySchema>;

export const ResellerSupportTicketSchema = z.object({
  id: z.string(),
  ticketNumber: z.string(),
  subject: z.string(),
  category: z.string(),
  priority: ResellerTicketPrioritySchema,
  status: ResellerTicketStatusSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
  lastMessageAt: z.string().nullable(),
});
export type ResellerSupportTicket = z.infer<typeof ResellerSupportTicketSchema>;

export const ResellerSupportTicketsResponseSchema = z.object({
  data: z.array(ResellerSupportTicketSchema),
  summary: ResellerSupportTicketSummarySchema,
  meta: PaginationMetaSchema,
});
export type ResellerSupportTicketsResponse = z.infer<typeof ResellerSupportTicketsResponseSchema>;

export const ResellerSupportTicketsParamsSchema = z.object({
  page: z.coerce.number().int().positive().optional(),
  limit: z.coerce.number().int().positive().max(100).optional(),
  status: z.string().optional(),
  priority: z.string().optional(),
});
export type ResellerSupportTicketsParams = z.infer<typeof ResellerSupportTicketsParamsSchema>;

export const ResellerTicketMessageSchema = z.object({
  id: z.string(),
  ticketId: z.string(),
  sender: z.enum(['PARTNER', 'STAFF']),
  senderName: z.string(),
  content: z.string(),
  createdAt: z.string(),
});
export type ResellerTicketMessage = z.infer<typeof ResellerTicketMessageSchema>;

export const ResellerTicketTimelineSchema = z.object({
  id: z.string(),
  status: ResellerTicketStatusSchema,
  changedAt: z.string(),
  changedBy: z.string(),
});
export type ResellerTicketTimeline = z.infer<typeof ResellerTicketTimelineSchema>;

export const ResellerSupportTicketDetailSchema = z.object({
  ticket: ResellerSupportTicketSchema,
  messages: z.array(ResellerTicketMessageSchema),
  timeline: z.array(ResellerTicketTimelineSchema),
});
export type ResellerSupportTicketDetail = z.infer<typeof ResellerSupportTicketDetailSchema>;

export const CreateResellerTicketSchema = z.object({
  subject: z.string().min(1).max(200),
  description: z.string().min(1).max(5000),
  category: z.string().min(1),
  priority: ResellerTicketPrioritySchema,
});
export type CreateResellerTicketRequest = z.infer<typeof CreateResellerTicketSchema>;

export const CreateResellerMessageSchema = z.object({
  content: z.string().min(1).max(5000),
});
export type CreateResellerMessageRequest = z.infer<typeof CreateResellerMessageSchema>;

// ============================================================================
// Commission Breakdown (P4B-09)
// ============================================================================

export const CommissionByTicketTypeSchema = z.object({
  ticketType: z.string(),
  totalCommission: z.number(),
  bookingCount: z.number().int(),
});
export type CommissionByTicketType = z.infer<typeof CommissionByTicketTypeSchema>;

export const CommissionOverTimeSchema = z.object({
  date: z.string(),
  commission: z.number(),
  bookingCount: z.number().int(),
});
export type CommissionOverTime = z.infer<typeof CommissionOverTimeSchema>;

export const CommissionPeriodComparisonSchema = z.object({
  current: z.number(),
  previous: z.number(),
  changePercent: z.number(),
});
export type CommissionPeriodComparison = z.infer<typeof CommissionPeriodComparisonSchema>;

export const CommissionBreakdownResponseSchema = z.object({
  byTicketType: z.array(CommissionByTicketTypeSchema),
  overTime: z.array(CommissionOverTimeSchema),
  periodComparison: CommissionPeriodComparisonSchema,
});
export type CommissionBreakdownResponse = z.infer<typeof CommissionBreakdownResponseSchema>;

export const CommissionBreakdownParamsSchema = z.object({
  dateFrom: z.string(),
  dateTo: z.string(),
  granularity: z.enum(['daily', 'weekly', 'monthly']).optional(),
});
export type CommissionBreakdownParams = z.infer<typeof CommissionBreakdownParamsSchema>;

// ============================================================================
// Promo Codes (P4C-01)
// ============================================================================

export const PromoCodeStatusSchema = z.enum(['ACTIVE', 'EXPIRED', 'DEPLETED']);
export type PromoCodeStatus = z.infer<typeof PromoCodeStatusSchema>;

export const PromoDiscountTypeSchema = z.enum(['PERCENTAGE', 'FIXED']);
export type PromoDiscountType = z.infer<typeof PromoDiscountTypeSchema>;

export const ResellerPromoCodeSchema = z.object({
  id: z.string(),
  code: z.string(),
  description: z.string(),
  discountType: PromoDiscountTypeSchema,
  discountValue: z.number(),
  validFrom: z.string(),
  validTo: z.string(),
  status: PromoCodeStatusSchema,
  maxRedemptions: z.number().int().nullable(),
  redemptionCount: z.number().int(),
  createdAt: z.string(),
});
export type ResellerPromoCode = z.infer<typeof ResellerPromoCodeSchema>;

export const ResellerPromoCodesResponseSchema = z.object({
  data: z.array(ResellerPromoCodeSchema),
  meta: PaginationMetaSchema,
});
export type ResellerPromoCodesResponse = z.infer<typeof ResellerPromoCodesResponseSchema>;

export const ResellerPromoCodesParamsSchema = z.object({
  page: z.coerce.number().int().positive().optional(),
  limit: z.coerce.number().int().positive().max(100).optional(),
  status: z.string().optional(),
});
export type ResellerPromoCodesParams = z.infer<typeof ResellerPromoCodesParamsSchema>;

export const ResellerPromoCodeUsageResponseSchema = z.object({
  totalRedemptions: z.number().int(),
  totalDiscountGiven: z.number(),
  usageByDate: z.array(
    z.object({
      date: z.string(),
      count: z.number().int(),
      discount: z.number(),
    }),
  ),
});
export type ResellerPromoCodeUsageResponse = z.infer<typeof ResellerPromoCodeUsageResponseSchema>;

// ============================================================================
// Analytics Overview (P4C-03)
// ============================================================================

export const ResellerAnalyticsOverviewResponseSchema = z.object({
  conversionRate: z.number(),
  retentionRate: z.number(),
  avgLeadTimeDays: z.number(),
  popularTicketTypes: z.array(
    z.object({
      ticketType: z.string(),
      bookingCount: z.number().int(),
      revenue: z.number(),
    }),
  ),
  peakBookingDays: z.array(
    z.object({
      dayOfWeek: z.number().int(),
      dayName: z.string(),
      bookingCount: z.number().int(),
    }),
  ),
  leadTimeDistribution: z.object({
    avg: z.number(),
    median: z.number(),
    p90: z.number(),
  }),
});
export type ResellerAnalyticsOverviewResponse = z.infer<typeof ResellerAnalyticsOverviewResponseSchema>;

export const ResellerAnalyticsOverviewParamsSchema = z.object({
  dateFrom: z.string(),
  dateTo: z.string(),
});
export type ResellerAnalyticsOverviewParams = z.infer<typeof ResellerAnalyticsOverviewParamsSchema>;

// ============================================================================
// Marketing Assets (P4C-05)
// ============================================================================

export const MarketingAssetCategorySchema = z.enum([
  'BANNER',
  'LOGO',
  'SOCIAL',
  'PRINT',
  'VIDEO',
]);
export type MarketingAssetCategory = z.infer<typeof MarketingAssetCategorySchema>;

export const MarketingAssetSchema = z.object({
  id: z.string(),
  name: z.string(),
  category: MarketingAssetCategorySchema,
  format: z.string(),
  fileSize: z.number().int(),
  thumbnailUrl: z.string(),
  downloadUrl: z.string(),
  createdAt: z.string(),
});
export type MarketingAsset = z.infer<typeof MarketingAssetSchema>;

export const MarketingAssetsResponseSchema = z.object({
  data: z.array(MarketingAssetSchema),
  meta: PaginationMetaSchema,
});
export type MarketingAssetsResponse = z.infer<typeof MarketingAssetsResponseSchema>;

export const MarketingAssetsParamsSchema = z.object({
  page: z.coerce.number().int().positive().optional(),
  limit: z.coerce.number().int().positive().max(100).optional(),
  category: z.string().optional(),
});
export type MarketingAssetsParams = z.infer<typeof MarketingAssetsParamsSchema>;

// ============================================================================
// SLA Metrics (P4C-09)
// ============================================================================

export const SlaIncidentSeveritySchema = z.enum(['MINOR', 'MAJOR', 'CRITICAL']);
export type SlaIncidentSeverity = z.infer<typeof SlaIncidentSeveritySchema>;

export const SlaIncidentSchema = z.object({
  id: z.string(),
  title: z.string(),
  severity: SlaIncidentSeveritySchema,
  startedAt: z.string(),
  resolvedAt: z.string().nullable(),
  durationMinutes: z.number().int().nullable(),
});
export type SlaIncident = z.infer<typeof SlaIncidentSchema>;

export const SlaMetricsResponseSchema = z.object({
  uptimePercent: z.number(),
  slaTarget: z.number(),
  slaMet: z.boolean(),
  avgResponseTimeMs: z.number(),
  webhookDeliveryRate: z.number(),
  webhookSuccessCount: z.number().int(),
  webhookFailCount: z.number().int(),
  responseTimeHistory: z.array(
    z.object({
      date: z.string(),
      avgMs: z.number(),
    }),
  ),
  incidents: z.array(SlaIncidentSchema),
});
export type SlaMetricsResponse = z.infer<typeof SlaMetricsResponseSchema>;

export const SlaMetricsPeriodSchema = z.enum(['7d', '30d', '90d']);
export type SlaMetricsPeriod = z.infer<typeof SlaMetricsPeriodSchema>;

export const SlaMetricsParamsSchema = z.object({
  period: SlaMetricsPeriodSchema,
});
export type SlaMetricsParams = z.infer<typeof SlaMetricsParamsSchema>;
